import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getNotifications, markNotificationRead, markAllNotificationsRead, deleteNotification } from '@/services/api';
import { Bell, Check, CheckCheck, Trash2, AlertTriangle, Shield, ClipboardCheck, FileText, MessageSquare, Users, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const NOTIFICATION_ICONS = {
  incident_assigned: AlertTriangle,
  incident_status_change: AlertTriangle,
  risk_assigned: Shield,
  risk_level_change: Shield,
  capa_assigned: ClipboardCheck,
  capa_status_change: ClipboardCheck,
  capa_due_soon: ClipboardCheck,
  capa_overdue: ClipboardCheck,
  audit_assigned: FileText,
  mention: MessageSquare,
  comment_reply: MessageSquare,
  meeting_invite: Users,
  meeting_reminder: Users,
  policy_pending_approval: FileText,
  policy_updated: FileText,
  system_alert: Bell,
};

const PRIORITY_COLORS = {
  critical: 'bg-red-500',
  high: 'bg-orange-500',
  medium: 'bg-blue-500',
  low: 'bg-slate-500',
};

export default function NotificationBell() {
  const { api, token, user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const wsRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    if (!api) return;
    setLoading(true);
    try {
      const response = await getNotifications(api, { limit: 20 });
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  // WebSocket connection for real-time notifications
  // Falls back to polling if WS upgrade is not supported by the deployment
  // (e.g. some preview ingresses don't proxy WebSockets — we give up after a
  // few failed attempts to avoid console spam, and polling continues to keep
  // the badge fresh).
  useEffect(() => {
    if (!token || !user) return;

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = process.env.REACT_APP_BACKEND_URL?.replace(/^https?:\/\//, '').replace(/\/api$/, '') || window.location.host;
    const wsUrl = `${wsProtocol}//${wsHost}/api/ws/notifications?token=${token}`;

    const MAX_RECONNECT_ATTEMPTS = 3;
    let reconnectAttempts = 0;
    let reconnectTimer = null;
    let pollTimer = null;
    let cancelled = false;

    const startPollingFallback = () => {
      if (pollTimer) return;
      // Quiet, low-frequency unread-count poll. fetchNotifications() already
      // runs once on mount; this keeps the badge fresh without WS.
      const poll = () => {
        if (cancelled) return;
        fetchNotifications();
        pollTimer = setTimeout(poll, 60000);
      };
      pollTimer = setTimeout(poll, 60000);
    };

    const connectWebSocket = () => {
      if (cancelled) return;
      try {
        wsRef.current = new WebSocket(wsUrl);

        wsRef.current.onopen = () => {
          reconnectAttempts = 0;
          if (pollTimer) { clearTimeout(pollTimer); pollTimer = null; }
        };

        wsRef.current.onmessage = (event) => {
          const data = JSON.parse(event.data);

          if (data.type === 'connected') {
            setUnreadCount(data.unread_count || 0);
          } else if (data.type === 'notification') {
            const notif = data.notification;
            setNotifications(prev => [notif, ...prev.slice(0, 19)]);
            setUnreadCount(prev => prev + 1);
            toast(notif.title, {
              description: notif.message,
              action: { label: 'View', onClick: () => setIsOpen(true) }
            });
          } else if (data.type === 'unread_count') {
            setUnreadCount(data.count || 0);
          }
        };

        wsRef.current.onclose = () => {
          if (cancelled) return;
          if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
            // Give up and fall back to polling — common in preview environments
            // where the ingress doesn't proxy WebSocket upgrades.
            startPollingFallback();
            return;
          }
          const backoff = Math.min(30000, 5000 * Math.pow(2, reconnectAttempts));
          reconnectAttempts += 1;
          reconnectTimer = setTimeout(connectWebSocket, backoff);
        };

        wsRef.current.onerror = () => {
          // onclose will run right after — handle reconnect/fallback there
          // (intentionally silent — onerror fires once per bad attempt and the
          // structured retry/fallback logic in onclose owns the user-visible
          // behaviour).
        };
      } catch (error) {
        // Same: defer to onclose flow
        if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
          startPollingFallback();
        }
      }
    };

    connectWebSocket();

    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (pollTimer) clearTimeout(pollTimer);
      if (wsRef.current) {
        try { wsRef.current.close(); } catch (e) { /* ignore */ }
      }
    };
  }, [token, user, fetchNotifications]);

  // Fetch notifications on mount
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMarkRead = async (id, e) => {
    e.stopPropagation();
    try {
      await markNotificationRead(api, id);
      setNotifications(prev => prev.map(n => 
        n.id === id ? { ...n, is_read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead(api);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    try {
      await deleteNotification(api, id);
      const notif = notifications.find(n => n.id === id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      if (notif && !notif.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const getTimeAgo = (dateStr) => {
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
    } catch {
      return '';
    }
  };

  const getIcon = (type) => {
    const Icon = NOTIFICATION_ICONS[type] || Bell;
    return Icon;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <Button
        variant="ghost"
        size="icon"
        className="relative text-slate-400 hover:text-white hover:bg-slate-800"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-bold bg-red-500 text-white rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </Button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-slate-900 border border-slate-800 rounded-lg shadow-xl z-50" data-testid="notification-dropdown">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
            <h3 className="text-white font-semibold">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-slate-400 hover:text-white h-8 px-2"
                  onClick={handleMarkAllRead}
                >
                  <CheckCheck className="w-4 h-4 mr-1" />
                  Mark all read
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-slate-400 hover:text-white"
                onClick={() => setIsOpen(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Notification List */}
          <ScrollArea className="max-h-[400px]">
            {loading && notifications.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div className="spinner w-6 h-6" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-slate-500">
                <Bell className="w-10 h-10 mb-2 opacity-50" />
                <p>No notifications</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-800">
                {notifications.map((notif) => {
                  const Icon = getIcon(notif.notification_type);
                  return (
                    <div
                      key={notif.id}
                      className={`flex gap-3 px-4 py-3 hover:bg-slate-800/50 cursor-pointer transition-colors ${
                        !notif.is_read ? 'bg-slate-800/30' : ''
                      }`}
                      onClick={(e) => {
                        if (!notif.is_read) handleMarkRead(notif.id, e);
                        // Navigate to entity if available
                        if (notif.entity_type && notif.entity_id) {
                          window.location.href = `/${notif.entity_type}s`;
                        }
                      }}
                      data-testid={`notification-item-${notif.id}`}
                    >
                      {/* Icon */}
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${PRIORITY_COLORS[notif.priority] || PRIORITY_COLORS.medium}`}>
                        <Icon className="w-4 h-4 text-white" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm ${notif.is_read ? 'text-slate-400' : 'text-white font-medium'}`}>
                            {notif.title}
                          </p>
                          {!notif.is_read && (
                            <span className="flex-shrink-0 w-2 h-2 bg-sky-500 rounded-full mt-1.5" />
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{notif.message}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-slate-600">{getTimeAgo(notif.created_at)}</span>
                          {notif.entity_number && (
                            <Badge variant="outline" className="text-xs px-1 py-0 border-slate-700 text-slate-500">
                              {notif.entity_number}
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex-shrink-0 flex flex-col gap-1">
                        {!notif.is_read && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-slate-500 hover:text-sky-400"
                            onClick={(e) => handleMarkRead(notif.id, e)}
                            title="Mark as read"
                          >
                            <Check className="w-3 h-3" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-slate-500 hover:text-red-400"
                          onClick={(e) => handleDelete(notif.id, e)}
                          title="Delete"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t border-slate-800">
              <Button
                variant="ghost"
                className="w-full text-slate-400 hover:text-white text-sm"
                onClick={() => {
                  setIsOpen(false);
                  window.location.href = '/settings';
                }}
              >
                Notification Settings
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
